import LoadingButton from '@atlaskit/button/loading-button';
import { Checkbox } from '@atlaskit/checkbox';
import Form, { CheckboxField, ErrorMessage, Field, Fieldset } from '@atlaskit/form';
import Lozenge from '@atlaskit/lozenge';
import Select, { OptionsType, ValueType } from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import * as React from 'react';
import { connect, useDispatch } from 'react-redux';
import { InvoiceWithLineItems, RefundReasonCode, RefundReasons } from '@atlassian/cssxp-types-billing';

import { fetchRefundActions, processRefund } from '@atlassian/temp-proxies';
import CreditCardDetail from './credit-card-detail';
import PaypalDetail from './paypal-detail';
import { ErrorMessageOnInfoPanel } from '@css-xp/base/error-page';
import { isCloudParent } from '@atlassian/cssxp-utils-product';
import {
  FormattedCurrency,
  InfoPanel,
  RequiredFieldMessage,
  successNotificationBuilder,
  errorNotificationBuilder,
  addNotification,
  useSidebar,
} from '@css-xp/base';
import * as invoiceUtils from '@atlassian/cssxp-utils-invoice';
import { useState, useEffect } from 'react';
import { logTimeStamp } from '@atlassian/temp-metal';

interface ReduxStateProps {
  isProcessingRefund: boolean;
  processRefundError: string | null;
  canBackdateMaintenance: boolean;
}

interface ComponentProps {
  invoice: InvoiceWithLineItems;
}

export type Props = ReduxStateProps & ComponentProps;

interface RefundLineItem {
  checked: boolean;
  entitlementId?: string;
  amount?: string;
  description?: string;
}

export enum ActionRefundMessage {
  DELETE_AND_BACKDATE = 'Delete the license for new licenses and backdate the expiry date to the previous billing ' +
    'period for renewed licenses.',
  DELETE = 'Delete the license so the users can no longer access it at my.atlassian.com.',
  BACKDATE = 'Backdate the expiry date for refunded products to the previous billing period.',
}

interface ReasonCodeSelect {
  label: string;
  value: RefundReasonCode;
}

export type RefundForm = {
  reasonCode: ReasonCodeSelect;
  notes: string;
  refundLineItemIds: string[];
  shouldActionRefund: boolean;
} & {
  [key: string]: string;
};
//@ts-ignore
const refundCodeOptions: OptionsType<ReasonCodeSelect> = Object.keys(RefundReasons).map((key: RefundReasonCode) => {
  return {
    label: RefundReasons[key],
    value: key,
  };
});

const convertCurrencyStringToNumber = (value: string): number => {
  return Number(defaultTo(value, '0'));
};

interface State {
  refundItems: RefundLineItem[];
  totalAmount: string;
  // Since the table isn't a typical field, doing a similar behavior outside of the form error state
  refundAmountZeroError: boolean;
  areAllItemsSelected: boolean;
  isActionRefundSelected: boolean;
  isActionRefundCheckboxShown: boolean;
  invoicePlatform: string;
  isEligibleForBackdate: boolean;
  isEligibleForLicenseDeletion: boolean;
}

type ValidationErrors = { [Field in keyof RefundForm]?: boolean };

export const ProcessRefundForm = ({ invoice }: ComponentProps) => {
  const state = {
    refundItems: [],
    totalAmount: '0.00',
    refundAmountZeroError: false,
    areAllItemsSelected: true,
    isActionRefundSelected: true,
    invoicePlatform: '',
    isActionRefundCheckboxShown: false,
    isEligibleForBackdate: false,
    isEligibleForLicenseDeletion: false,
  };
  const dispatch = useDispatch();
  const { closeSidebar } = useSidebar();
  const [invoicestate, setInvoicestate] = useState<any>(state);
  const [isProcessingRefund, setIsProcessingRefund] = React.useState(false);
  const [processRefundError, setProcessRefundError] = React.useState<string | Error>('');
  const setRefundActionCheckbox = async (invoiceId: string) => {
    const { isEligibleForBackdate, isEligibleForLicenseDeletion } = await fetchRefundActions(invoiceId);
    const isActionRefundCheckboxShown = isEligibleForBackdate || isEligibleForLicenseDeletion ? true : false;

    setInvoicestate({
      ...invoicestate,
      isActionRefundCheckboxShown: isActionRefundCheckboxShown,
      isEligibleForBackdate: isEligibleForBackdate,
      isEligibleForLicenseDeletion: isEligibleForLicenseDeletion,
    });
  };
  const setPlatformType = async () => {
    if (invoice.lineItems.every((lineItem) => lineItem.cloudSiteHostname)) {
      setInvoicestate({ ...invoicestate, invoicePlatform: 'cloud' });
    } else if (invoice.lineItems.every((lineItem) => !lineItem.cloudSiteHostname)) {
      setInvoicestate({ ...invoicestate, invoicePlatform: 'server' });
    }
  };
  const updateRefundItems = invoice
    ? invoice.lineItems
        .filter((lineItem) => lineItem.sen)
        .filter((lineItem) => !isCloudParent(lineItem.productKey))
        .map((lineItem) => {
          const isRefunded =
            lineItem.invoiceLineItemId && invoiceUtils.isInvoiceLineItemRefunded(invoice, lineItem.invoiceLineItemId);
          return {
            checked: true,
            amount: isRefunded ? '0.00' : lineItem.amount ?? undefined,
            description: lineItem.description ?? undefined,
            entitlementId: lineItem.sen ?? undefined,
            invoiceLineItemId: lineItem.invoiceLineItemId ?? undefined,
          };
        })
    : [];

  let amount = 0;
  updateRefundItems.forEach((item) => (amount += Number(item.amount)));
  //@ts-ignore
  useEffect(async () => {
    await setInvoicestate({ ...invoicestate, refundItems: updateRefundItems, totalAmount: amount.toFixed(2) });
    await setRefundActionCheckbox(invoice.id);
    await setPlatformType();
  }, []);

  const renderRefundedLozenge = () => {
    return (
      <Lozenge appearance={'removed'} isBold={false}>
        Refunded
      </Lozenge>
    );
  };
  const updateRefundProductSelection = async (getValues: () => RefundForm) => {
    const formValues = getValues();
    const refundItems = formValues.refundLineItemIds ? formValues.refundLineItemIds : [];

    const checkedItems = refundItems
      ? refundItems.map((item) => {
          return convertCurrencyStringToNumber(formValues[`value${item}`]);
        })
      : [];

    const areAllItemsSelected = checkedItems.length === invoicestate.refundItems.length;

    const amount = checkedItems.length > 0 ? checkedItems.reduce((a, b) => a + b) : 0;

    setInvoicestate({ ...invoicestate, areAllItemsSelected, totalAmount: amount.toFixed(2) });
  };
  const renderRefundReasonDropdown = () => {
    return (
      <>
        <Field<ValueType<ReasonCodeSelect>> name="reasonCode" label="Refund reason" isRequired>
          {({ fieldProps, error }) => (
            <div>
              <Select<ReasonCodeSelect>
                {...fieldProps}
                id={fieldProps.id}
                spacing="compact"
                isSearchable={false}
                options={refundCodeOptions}
                placeholder="Select the reason for this refund"
                validationState={error ? 'error' : 'default'}
              />
              {error && <ErrorMessage>Please select the reason for refund</ErrorMessage>}
            </div>
          )}
        </Field>
      </>
    );
  };
  const renderPaymentInfo = () => {
    return (
      <Fieldset legend="Refund to payment method">
        <div>
          {invoiceUtils.wasPaidWithCreditCard(invoice) && <CreditCardDetail creditCard={invoice.creditCard} />}
          {invoiceUtils.wasPaidWithPaypal(invoice) && invoice.paypalAccountEmail && (
            <PaypalDetail paypalAccountEmail={invoice.paypalAccountEmail} />
          )}
        </div>
      </Fieldset>
    );
  };
  const OnFormSubmit = (refundFormData: RefundForm) => {
    logTimeStamp(new Date().getTime(), 'Process Refund', 'fe.user.task.process.refund.time');
    let refundAmountZeroError = false;

    if (
      !refundFormData.refundLineItemIds ||
      Number(invoicestate.totalAmount) === 0 ||
      !refundFormData.reasonCode ||
      !refundFormData.notes
    ) {
      const errors: ValidationErrors = {};

      if (!refundFormData.refundLineItemIds || Number(invoicestate.totalAmount) <= 0) {
        refundAmountZeroError = true;
      }

      if (!refundFormData.reasonCode) {
        errors.reasonCode = true;
      }

      if (!refundFormData.notes) {
        errors.notes = true;
      }

      setInvoicestate({ ...invoicestate, refundAmountZeroError: refundAmountZeroError });

      return errors;
    }

    setInvoicestate({ ...invoicestate, refundAmountZeroError: refundAmountZeroError });

    const cleanedRefundItems = refundFormData.refundLineItemIds.map((entitlementId) => {
      const totalAmountExTax = refundFormData[`value${entitlementId}`];

      return {
        entitlementId,
        totalAmountExTax,
      };
    });

    const processRefundInvoice = async () => {
      let notification;
      try {
        const refundActionResult = await processRefund(invoice.id, {
          refundItems: cleanedRefundItems,
          notes: refundFormData.notes,
          reasonCode: refundFormData.reasonCode.value,
          shouldActionRefund: refundFormData.shouldActionRefund,
        });

        const notification = successNotificationBuilder()
          .withHeader('Expiration backdate succeeded')
          .withMessage('The refunded product(s) have been successfully backdated')
          .build();
        dispatch(addNotification(notification));
        setIsProcessingRefund(true);
        closeSidebar();
      } catch (err: any) {
        notification = errorNotificationBuilder()
          .withHeader('Error')
          .withMessage(
            `We experienced an issue making updates to the license. In the meantime, please
            <a target="_blank" rel="noopener" href="/api/1/redirects/ada">
              update expiration dates or delete licenses
            </a> directly in ADA.`
          )
          .build();
        dispatch(addNotification(notification));
        setProcessRefundError(err.message);
      }
    };
    if (!isProcessingRefund) {
      processRefundInvoice();
    }
  };
  const renderNotes = () => {
    const maxCharacters = 300;
    return (
      <Field name="notes" label="Notes" isRequired>
        {({ fieldProps, error }) => (
          <>
            {
              <TextArea
                {...fieldProps}
                placeholder="Please include the support ticket number"
                // maxLength exists on textarea, but not TextArea
                // @ts-ignore
                maxLength={maxCharacters}
              />
            }
            {error && <ErrorMessage>Please add a note to the refund</ErrorMessage>}
          </>
        )}
      </Field>
    );
  };

  const renderSelectItemsToBeRefunded = (formValues: any) => {
    const { refundItems, totalAmount, refundAmountZeroError } = invoicestate;
    return (
      <div data-testid="form">
        <table className="refund-items">
          <thead>
            <tr>
              <th>
                <Fieldset legend="Select items to be refunded">
                  <span />
                </Fieldset>
              </th>
              <th />
              <th style={{ whiteSpace: 'nowrap' }}>
                <Fieldset legend="Refund amount">
                  <span />
                </Fieldset>
              </th>
            </tr>
          </thead>
          <tbody>
            {/*@ts-ignore*/}
            {refundItems.map((lineItem) => {
              const isRefunded = invoiceUtils.isInvoiceLineItemRefunded(invoice, get(lineItem, 'invoiceLineItemId'));

              return (
                <tr key={`${lineItem.entitlementId}`}>
                  <td>
                    <CheckboxField
                      name="refundLineItemIds"
                      isDisabled={isRefunded}
                      value={lineItem.entitlementId}
                      defaultIsChecked={!isRefunded}
                    >
                      {({ fieldProps }) => (
                        <Checkbox
                          {...fieldProps}
                          label={lineItem.description || 'Description not provided'}
                          onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
                            fieldProps.onChange(event);
                            await updateRefundProductSelection(formValues);
                          }}
                          testId="line-item-checkbox"
                        />
                      )}
                    </CheckboxField>
                  </td>
                  <td className="refund-item-status">{isRefunded ? renderRefundedLozenge() : null}</td>
                  <td>
                    <Field
                      name={`value${lineItem.entitlementId}`}
                      defaultValue={lineItem.amount}
                      transform={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const inputValue = event.currentTarget.value;
                        const maxLineItemAmount = defaultTo(lineItem.amount, 0);
                        if (Number(inputValue) > Number(maxLineItemAmount)) {
                          return maxLineItemAmount;
                        } else if (Number(inputValue) < 0) {
                          return 0;
                        }
                        return inputValue;
                      }}
                    >
                      {({ fieldProps }) => (
                        <Textfield
                          {...fieldProps}
                          isCompact={true}
                          isDisabled={isRefunded}
                          style={{
                            textAlign: 'right',
                          }}
                          type="number"
                          min="0.00"
                          max={Number(defaultTo(lineItem.amount, '0')).toFixed(2)}
                          step="0.01"
                          onChange={(event) => {
                            fieldProps.onChange(event);
                            updateRefundProductSelection(formValues);
                          }}
                        />
                      )}
                    </Field>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '1px solid black' }}>
              <td>
                <strong>Total refund amount excl. tax</strong>
              </td>
              <td />
              <td
                style={{
                  textAlign: 'right',
                  paddingRight: '8px',
                  whiteSpace: 'nowrap',
                }}
              >
                <strong>
                  <FormattedCurrency
                    currency={invoice && invoice.paymentCurrency}
                    showCurrencyCode={true}
                    value={totalAmount}
                  />
                </strong>
              </td>
            </tr>
          </tfoot>
        </table>
        {refundAmountZeroError && <ErrorMessage>Total refund amount should more than 0</ErrorMessage>}
      </div>
    );
  };
  const renderActionRefundCheckbox = () => {
    const {
      areAllItemsSelected,
      isActionRefundSelected,
      invoicePlatform,
      isEligibleForBackdate,
      isEligibleForLicenseDeletion,
    } = invoicestate;

    const showNotCheckedWarning: boolean = !isActionRefundSelected || !areAllItemsSelected;

    let actionCheckboxLabel =
      'Delete new licenses and backdate renewed licenses so they are no longer accessible by the customer.';
    if (!isEligibleForBackdate && isEligibleForLicenseDeletion) {
      actionCheckboxLabel = ActionRefundMessage.DELETE;
    } else if (isEligibleForBackdate && !isEligibleForLicenseDeletion) {
      actionCheckboxLabel = ActionRefundMessage.BACKDATE;
    }

    const serverLicenseUI = {
      warningMessage: `
                The license for refunded products will not be deleted or
                backdated and customers may continue to access the license at
                my.atlassian.com.`,
      label: actionCheckboxLabel,
    };

    const cloudLicenseUI = {
      warningMessage: `
              The expiry date for refunded products will not be backdated and
              customers may continue to access the product.`,
      label: `Backdate the expiry date for refunded products to the previous billing period.`,
    };

    const labelText = invoicePlatform === 'cloud' ? cloudLicenseUI.label : serverLicenseUI.label;
    const warningMessage = invoicePlatform === 'cloud' ? cloudLicenseUI.warningMessage : serverLicenseUI.warningMessage;

    return (
      <>
        <CheckboxField name="shouldActionRefund" defaultIsChecked={true}>
          {({ fieldProps }) => (
            <Checkbox
              {...fieldProps}
              label={labelText}
              isChecked={areAllItemsSelected && isActionRefundSelected}
              isDisabled={!areAllItemsSelected}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const backdateCheckbox = event.target;
                fieldProps.onChange(event);
                setInvoicestate({ ...invoicestate, isActionRefundSelected: backdateCheckbox.checked });
              }}
              testId="action-refund-checkbox"
            />
          )}
        </CheckboxField>
        {showNotCheckedWarning && (
          <div style={{ marginTop: '10px' }} data-testid="action-refund-checkbox-warning">
            <InfoPanel type="warning" message={warningMessage} />
          </div>
        )}
      </>
    );
  };
  const { isActionRefundCheckboxShown } = invoicestate;
  return (
    <div className="process-refunds-form">
      <h3>Process refund - {invoice.id}</h3>
      <RequiredFieldMessage />
      <Form onSubmit={OnFormSubmit}>
        {({ formProps, getValues }) => (
          <form {...formProps} id="refund-form">
            {renderRefundReasonDropdown()}
            {renderSelectItemsToBeRefunded(getValues)}
            {renderPaymentInfo()}
            {renderNotes()}
            {isActionRefundCheckboxShown && renderActionRefundCheckbox()}
            <div style={{ marginTop: '16px' }}>
              <LoadingButton appearance="primary" type="submit" isLoading={isProcessingRefund} formNoValidate>
                Process refund
              </LoadingButton>
            </div>
            {processRefundError && (
              <InfoPanel
                type="error"
                message={
                  <ErrorMessageOnInfoPanel
                    title="We're unable to process the refund for the following reason"
                    message="Error"
                  />
                }
              />
            )}
          </form>
        )}
      </Form>
    </div>
  );
};
